import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { FilterBar } from '@/components/shared/filter-bar';
import { Pagination } from '@/components/shared/pagination';
import { SearchInput } from '@/components/shared/search-input';
import {
  ExpenseExternalPartyDialog,
  type ExpenseExternalParty,
} from '@/components/spending/expense-external-party-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Listbox } from '@/components/ui/listbox';
import { api, type Collection } from '@/lib/api';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';

const STATUS_ANY = '__any_status__';

export interface ExpenseExternalPartiesPanelProps {
  createOpen: boolean;
  onCreateOpenChange: (open: boolean) => void;
}

export const ExpenseExternalPartiesPanel = ({
  createOpen,
  onCreateOpenChange,
}: ExpenseExternalPartiesPanelProps) => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const membership = useChurchStore((state) => state.membership);
  const canManage = hasPermission(membership, PERMISSIONS.EXPENSES_CREATE);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState(STATUS_ANY);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [editing, setEditing] = useState<ExpenseExternalParty | null>(null);

  useEffect(() => {
    if (createOpen) setEditing(null);
  }, [createOpen]);

  const list = useQuery({
    queryKey: [
      'expenses',
      'external-parties',
      'manage',
      page,
      pageSize,
      search,
      filterStatus,
    ],
    queryFn: () =>
      api.get<Collection<ExpenseExternalParty>>('/expenses/external-parties', {
        query: {
          page,
          pageSize,
          q: search.trim() || undefined,
          includeInactive: true,
          status: filterStatus === STATUS_ANY ? undefined : filterStatus,
        },
      }),
    placeholderData: keepPreviousData,
  });

  const rows = list.data?.data ?? [];

  const appliedCount = useMemo(() => {
    let count = 0;
    if (search.trim()) count += 1;
    if (filterStatus !== STATUS_ANY) count += 1;
    return count;
  }, [filterStatus, search]);

  const columns: DataTableColumn<ExpenseExternalParty>[] = [
    {
      id: 'name',
      header: t('externalParties.columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'status',
      header: t('externalParties.columns.status'),
      cell: (row) => (
        <Badge variant={row.isActive ? 'success' : 'neutral'}>
          {row.isActive ? tCommon('status.active') : tCommon('status.inactive')}
        </Badge>
      ),
    },
  ];

  const openEdit = (row: ExpenseExternalParty) => {
    if (!canManage) return;
    setEditing(row);
  };

  return (
    <div className="space-y-3">
      <FilterBar
        appliedCount={appliedCount}
        onClearAll={() => {
          setSearch('');
          setFilterStatus(STATUS_ANY);
          setPage(1);
        }}
        labels={{
          filters: t('externalParties.filters.title'),
          clearAll: t('externalParties.filters.clearAll'),
          applied: (count) => t('externalParties.filters.applied', { count }),
        }}
        leading={
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={t('externalParties.filters.search')}
            clearLabel={tCommon('search.clear')}
            className="max-w-none sm:w-56"
            aria-label={t('externalParties.filters.search')}
          />
        }
      >
        {({ popoverPortal }) => (
          <div className="w-44 min-w-[10rem]">
            <Listbox
              value={filterStatus}
              onChange={(value) => {
                setFilterStatus(value ?? STATUS_ANY);
                setPage(1);
              }}
              options={[
                { value: STATUS_ANY, label: t('externalParties.filters.statusAny') },
                { value: 'active', label: tCommon('status.active') },
                { value: 'inactive', label: tCommon('status.inactive') },
              ]}
              popoverPortal={popoverPortal}
            />
          </div>
        )}
      </FilterBar>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        onRowClick={canManage ? openEdit : undefined}
        empty={
          <p className="text-body-sm text-fg-secondary">
            {appliedCount > 0 ? t('externalParties.emptyFiltered') : t('externalParties.empty')}
          </p>
        }
        rowActions={
          canManage
            ? (row) => (
                <div className="flex justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(event) => {
                      event.stopPropagation();
                      openEdit(row);
                    }}
                  >
                    {tCommon('action.edit')}
                  </Button>
                </div>
              )
            : undefined
        }
        actionsHeader={canManage ? tCommon('table.actions') : undefined}
      />

      {list.data?.page && list.data.page.totalPages > 1 ? (
        <Pagination
          page={list.data.page.page}
          pageSize={list.data.page.pageSize}
          totalItems={list.data.page.totalItems}
          totalPages={list.data.page.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          labels={{
            showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
        />
      ) : null}

      <ExpenseExternalPartyDialog
        open={createOpen || Boolean(editing)}
        onOpenChange={(next) => {
          if (!next) {
            onCreateOpenChange(false);
            setEditing(null);
          }
        }}
        party={editing}
      />
    </div>
  );
};
